// Presence state shape + the DOM anchor codec shared by the capture and
// overlay parts of the presence plugin. The wire envelope (clientId, name,
// color) is added by the collaboration server; the server treats the state as
// opaque, so THIS file is the source of truth for what's inside.
//
// Every DOM selector presence relies on is centralized here: if the Creator
// ever renames an attribute, this is the only file to fix.

// What kind of DOM node a cursor anchor points at:
//  - "el"     designer question/panel adorner  [data-sv-drop-target-survey-element=n]
//  - "page"   designer page adorner            [data-sv-drop-target-survey-page=n]
//  - "pg"     property-grid field              .svc-side-bar [data-name=n]
//  - "q"      inner-survey question of a tab   #scrollableDiv-{tab} [data-name=n]
//  - "tabbar" a tab strip item                 #tab-{n}
//  - "surface" the designer canvas content     #scrollableDiv-{tab} .svc-tab-designer_content
//  - "root"   the tab's scroll container       #scrollableDiv-{tab}
export type AnchorScope = "el" | "page" | "pg" | "q" | "tabbar" | "surface" | "root";

export interface IAnchorRef {
  s: AnchorScope;
  n?: string;
}

// The peer's keyboard focus - at most one per participant (the caret is
// singular; the type encodes that invariant), discriminated by the UI area
// it lives in:
//  - "pg"   a property-grid field. The grid flavor derives from `tab`
//           ("theme" -> the theme grid, anything else -> the main grid);
//           the main grid always shows the SELECTED object, so the target
//           object is the one in `sel`.
//  - "edit" an inline string editor on the designer surface (question/page/
//           survey title or description, a choice text, ...), addressed by
//           its owner plus the editor's document-order index inside it -
//           see `encodeEditFocus`/`resolveEditFocus`.
//  - "tr"   a Translations-tab cell (a comment textarea in the strings
//           table); `loc`+`p` are the portable identity, `m` is the sender's
//           matrix name kept as the same-view fast path.
export type IPresenceFocus =
  | {
    area: "pg",
    // Question name in the grid survey == property name.
    prop: string,
  }
  | {
    area: "edit",
    // Owner kind: an element adorner, a page adorner, or the survey header.
    scope: "el" | "page" | "survey",
    // Element/page name for el/page scopes; "" for the survey scope.
    name: string,
    // Index of the `.svc-string-editor` within the owner (document order).
    idx: number,
  }
  | {
    area: "tr",
    // Sender's stringsSurvey matrix name - fast path when both peers see the same table.
    m: string,
    // Column name: the locale code, or "default" for the default locale.
    l: string,
    // Locator of the owning element (TranslationItem.context); null when not encodable.
    loc: string | null,
    // Property name (TranslationItem.name), e.g. "title".
    p: string,
  };

// The local participant's ephemeral state as captured by `PresenceCapture`.
// Identity (name/color) is NOT part of the state - the transport layer is
// expected to attach it to the envelope (the collaboration server stamps
// `clientId`, `name` and `color` onto every relayed peer entry).
//
// Protocol invariant: everything below describes what the peer does NOW on
// `tab`. A tab switch resets every channel atomically in one update (and
// re-announces the selection when the designer returns), so receivers never
// see a channel that outlived its tab.
export interface IPresenceState {
  // creator.activeTab (viewType): designer|preview|theme|json|logic|translation.
  // Receivers compare it with their own activeTab to gate cursors. The DOM
  // container key (activeTabId) is deliberately NOT sent: after a same-view
  // check the peer's anchors resolve inside the receiver's own active tab
  // container, so each side uses its local activeTabId.
  tab: string;
  // Selected element, or null when nothing is selected. Shared only while
  // the sender is on the designer tab (cleared on leave, re-announced from
  // the model on return) - the ring receivers draw is always a ring the
  // sender is really looking at.
  sel: null | {
    // JournalLocator JSON pointer; "" = the survey itself.
    loc: string,
    // element.name - direct DOM anchor; null for non-anchorable objects.
    name: string | null,
  };
  // Keyboard focus, or null - see `IPresenceFocus`.
  focus: null | IPresenceFocus;
  // Sticky Translations-tab locale: the last locale column whose cell the
  // user focused. Unlike `focus` it survives the cell blur and stays while
  // the user remains on the Translations tab; it resets atomically with the
  // tab switch, like every other channel. "default" = the default locale
  // (same encoding as the "tr" focus `l`). Optional for wire compatibility
  // with older senders - readers must treat absence as null.
  trLoc?: string | null;
  // Mouse cursor, or null when the mouse left the creator. Always captured on
  // the active tab, so `tab` above is the view the cursor belongs to. Carries
  // no change stamp: the sender dedupes identical consecutive cursors, so
  // receivers treat changed content as a move (see the overlay's idle fade).
  cur: null | {
    a: IAnchorRef,
    // Position inside the anchor's border box, fractions 0..1. Omitted for
    // "surface" anchors when the px offsets below were computed; kept as the
    // fallback for the rare capture where the canvas block had no size.
    x?: number,
    y?: number,
    // "surface" anchors only: SIGNED offsets from the survey canvas block's
    // top-left corner (see `getCanvasElement`) in zoom-normalized px - the
    // on-screen distance divided by the sender's `survey.widthScale / 100`.
    // May be negative or exceed `w`/`h` when the cursor hovers the gutters
    // around the canvas. Integers. Receivers prefer these over the x/y
    // fractions (see `mapOffset`).
    px?: number,
    py?: number,
    // Zoom-normalized size of the sender's canvas block, integer px.
    w?: number,
    h?: number,
  };
}

// What the transport feeds into `CollaborationPlugin.upsertPeer`/`setPeers`.
export interface IPresencePeerEntry {
  clientId: string;
  // Display name, stamped by the server from the connection URL.
  name: string;
  // Server-assigned hex color.
  color: string;
  state: IPresenceState;
}

// A remote participant as stored in the roster. Identity (`name`/`color`)
// comes from the server envelope; liveness is the server's job, so the
// roster keeps nothing beyond what the transport delivered.
export type IPresencePeer = IPresencePeerEntry;

// ---------------------------------------------------------------------------
// Centralized selectors

// data-sv-drop-target-survey-element values that are drag&drop ghosts, not elements.
const GHOST_ELEMENT_NAMES = new Set(["sv-drag-drop-ghost-survey-element-name", "newGhostPage"]);

const cssEsc = (s: string): string =>
  typeof CSS !== "undefined" && CSS.escape ? CSS.escape(s) : s.replace(/["\\]/g, "\\$&");

export const PRESENCE_SELECTORS = {
  element: (name: string): string => `[data-sv-drop-target-survey-element="${cssEsc(name)}"]`,
  page: (name: string): string => `[data-sv-drop-target-survey-page="${cssEsc(name)}"]`,
  sidebar: ".svc-side-bar",
  sidebarPanel: ".svc-side-bar__container",
  // Sidebar modes whose panel floats OVER the tab content (z-index 1000).
  sidebarOverlaying: ".svc-side-bar--flyout, .svc-side-bar--mobile",
  toolbox: ".svc-toolbox",
  designerSurface: ".svc-tab-designer",
  designerSurfaceContent: ".svc-tab-designer_content",
  designerCanvasBlock: ".svc-designer-surface",
  dataName: (name: string): string => `[data-name="${cssEsc(name)}"]`,
  pgQuestionContent: ".spg-question__content",
  pgBooleanRow: "spg-question--boolean",
  pgCheckbox: ".sd-checkbox__decorator",
  stringEditor: ".svc-string-editor",
  // Translations strings-table locale cell (td) - one per locale column.
  translationCell: ".st-table__cell:not(.st-table__cell--row-text)",
  designerHeader: ".svc-designer-header",
  tabItem: (tabId: string): string => `#${cssEsc(`tab-${tabId}`)}`,
  tabContent: (tabId: string): string => `#${cssEsc(`scrollableDiv-${tabId}`)}`,
  tabbedMenu: ".svc-tabbed-menu"
};

// ---------------------------------------------------------------------------
// Anchor codec - encode on the capturing side, resolve on the rendering side.
// Both sides must agree, which is why they live in one file.

// Encode the most specific stable anchor for a point over `target`.
// `activeTabId` scopes the "q"/"root" fallbacks to the current tab.
export function encodeAnchor(target: Element, activeTabId: string): { a: IAnchorRef, el: Element } | null {
  const el = target.closest("[data-sv-drop-target-survey-element]");
  if (el) {
    const n = el.getAttribute("data-sv-drop-target-survey-element") ?? "";
    if (n && !GHOST_ELEMENT_NAMES.has(n)) return { a: { s: "el", n }, el };
  }
  const page = target.closest("[data-sv-drop-target-survey-page]");
  if (page) {
    const n = page.getAttribute("data-sv-drop-target-survey-page") ?? "";
    if (n && !GHOST_ELEMENT_NAMES.has(n)) return { a: { s: "page", n }, el: page };
  }
  const named = target.closest("[data-name]");
  if (named) {
    const n = named.getAttribute("data-name") ?? "";
    if (n) {
      if (named.closest(PRESENCE_SELECTORS.sidebar)) return { a: { s: "pg", n }, el: named };
      if (named.closest(PRESENCE_SELECTORS.tabContent(activeTabId))) return { a: { s: "q", n }, el: named };
    }
  }
  const tabItem = target.closest('[id^="tab-"]');
  if (tabItem && (tabItem.getAttribute("role") === "tab" || tabItem.classList.contains("svc-tabbed-menu-item"))) {
    return { a: { s: "tabbar", n: tabItem.id.slice("tab-".length) }, el: tabItem };
  }
  // The designer canvas, not the whole tab container: the container also holds
  // the toolbox, whose width differs between peers (expanded vs compact), so
  // fractions relative to it would not line up. Fractions are taken from the
  // SCROLLED content box, not the visible scroll viewport - the viewport's
  // rect depends on the peer's window size and scroll position, while the
  // content lays out the same survey the same way on every peer.
  const surface = target.closest(PRESENCE_SELECTORS.designerSurface);
  if (surface && surface.closest(PRESENCE_SELECTORS.tabContent(activeTabId))) {
    const content = surface.querySelector(PRESENCE_SELECTORS.designerSurfaceContent);
    return { a: { s: "surface" }, el: content ?? surface };
  }
  const container = target.closest(PRESENCE_SELECTORS.tabContent(activeTabId));
  if (container) return { a: { s: "root" }, el: container };
  return null;
}

// Encode the focused inline string editor for a node inside one; null when
// `target` is not inside a designer string editor. The editor is addressed by
// its owner (the innermost element adorner, a page adorner, or the survey
// header) plus its document-order index among the owner's editors - the
// designer DOM is generated from the same survey model on every client, so
// the index is stable across peers.
export function encodeEditFocus(target: Element): Extract<IPresenceFocus, { area: "edit" }> | null {
  const editor = target.closest(PRESENCE_SELECTORS.stringEditor);
  if (!editor) return null;
  let scope: "el" | "page" | "survey" = "el";
  let owner = editor.closest("[data-sv-drop-target-survey-element]");
  let name = owner?.getAttribute("data-sv-drop-target-survey-element") ?? "";
  if (!owner) {
    scope = "page";
    owner = editor.closest("[data-sv-drop-target-survey-page]");
    name = owner?.getAttribute("data-sv-drop-target-survey-page") ?? "";
  }
  if (!owner) {
    scope = "survey";
    owner = editor.closest(PRESENCE_SELECTORS.designerHeader);
    name = "";
  }
  if (!owner) return null;
  if (scope !== "survey" && (!name || GHOST_ELEMENT_NAMES.has(name))) return null;
  const editors = owner.querySelectorAll(PRESENCE_SELECTORS.stringEditor);
  for (let i = 0; i < editors.length; i++) {
    if (editors[i] === editor) return { area: "edit", scope, name, idx: i };
  }
  return null;
}

// Resolve an edit-focus anchor back to its `.svc-string-editor` node.
export function resolveEditFocus(edit: Extract<IPresenceFocus, { area: "edit" }>, root: ParentNode): Element | null {
  const owner = edit.scope === "el" ? root.querySelector(PRESENCE_SELECTORS.element(edit.name))
    : edit.scope === "page" ? root.querySelector(PRESENCE_SELECTORS.page(edit.name))
      : root.querySelector(PRESENCE_SELECTORS.designerHeader);
  if (!owner) return null;
  const editors = owner.querySelectorAll(PRESENCE_SELECTORS.stringEditor);
  return edit.idx >= 0 && edit.idx < editors.length ? editors[edit.idx] : null;
}

// Resolve an anchor back to a DOM node; null when it doesn't exist (yet).
// `root` is the node to search under (normally the document).
export function resolveAnchor(a: IAnchorRef, tabId: string, root: ParentNode): Element | null {
  const scoped = (sel: string): Element | null => {
    const container = root.querySelector(PRESENCE_SELECTORS.tabContent(tabId));
    return container ? container.querySelector(sel) : null;
  };
  switch(a.s) {
    case "el": return a.n ? scoped(PRESENCE_SELECTORS.element(a.n)) : null;
    case "page": return a.n ? scoped(PRESENCE_SELECTORS.page(a.n)) : null;
    case "pg": return a.n
      ? root.querySelector(`${PRESENCE_SELECTORS.sidebar} ${PRESENCE_SELECTORS.dataName(a.n)}`)
      : null;
    case "q": return a.n ? scoped(PRESENCE_SELECTORS.dataName(a.n)) : null;
    case "tabbar": return a.n ? root.querySelector(PRESENCE_SELECTORS.tabItem(a.n)) : null;
    case "surface": return scoped(PRESENCE_SELECTORS.designerSurfaceContent) ?? scoped(PRESENCE_SELECTORS.designerSurface);
    case "root": return root.querySelector(PRESENCE_SELECTORS.tabContent(tabId));
    default: return null;
  }
}

// The survey canvas block inside the designer content box: the node that
// wraps the survey header and the page adorners. Unlike the content box it
// honors the survey's width (`maxWidth = survey.renderedWidth`) and centers
// via `margin: auto`, so it lays out identically on every peer whenever the
// survey width is fixed - which makes it the reference corner for the
// px-offset cursor encoding. Falls back to `content` itself (placeholder
// mode, or markup this codec doesn't recognize).
export function getCanvasElement(content: Element): Element {
  const block = content.querySelector(PRESENCE_SELECTORS.designerCanvasBlock);
  if (block) return block;
  // Structural fallbacks in case the class ever changes: the block is the
  // node wrapping the survey header / the page adorners.
  const header = content.querySelector(PRESENCE_SELECTORS.designerHeader);
  if (header && header.parentElement && header.parentElement !== content) return header.parentElement;
  const page = content.querySelector("[data-sv-drop-target-survey-page]");
  if (page && page.parentElement && page.parentElement !== content) return page.parentElement;
  return content;
}

// Width of the near-edge zones treated as fixed-offset layout: covers the
// canvas paddings and the header title indent on the left, and the per-element
// action buttons on the right.
const EDGE_PX = 120;

// Map a zoom-normalized offset captured inside a sender box of size `ws` onto
// a receiver box of size `wr` (one axis; used for both). Equal boxes (fixed
// survey width, equal windows) pass px through untouched - exact mapping.
// Differing boxes (responsive survey width) preserve px distance from the
// nearest edge, where layout sits at fixed offsets, and interpolate
// proportionally across the stretchy middle; the piecewise branches agree at
// the zone borders, so the mapped cursor never jumps. Signed offsets (the
// gutters around the canvas) keep their distance beyond the box edges.
//
// Known limitation: RTL locales are right-anchored, so the left zone there is
// as approximate as the right one is in LTR - no worse than the fraction
// mapping this replaces.
export function mapOffset(d: number, ws: number, wr: number): number {
  if (ws === wr) return d;
  if (d < 0) return d;
  if (d > ws) return wr + (d - ws);
  const edge = Math.min(EDGE_PX, ws / 2, wr / 2);
  if (d <= edge) return d;
  if (ws - d <= edge) return wr - (ws - d);
  return edge + (d - edge) * (wr - 2 * edge) / (ws - 2 * edge);
}

// ---------------------------------------------------------------------------
// Small display helpers

// A participant who is doing nothing observable. Used as the capture's initial
// value and as what a collaboration plugin with presence switched off reports.
export function emptyPresenceState(): IPresenceState {
  return { tab: "", sel: null, focus: null, trLoc: null, cur: null };
}

// The theme's user-color slot for a participant: 1..9 and 0, matching the
// --sjs2-color-utility-user-{bg,fg-on,border}-color-N token family.
//
// Derived from the clientId rather than taken from the server envelope so that
// the color is theme-aware (the token family carries a legible foreground for
// each slot, which a raw hex cannot) and so that every client independently
// arrives at the SAME slot for the same peer - the derivation is deterministic,
// so no agreement protocol is needed.
export function presenceColorSlot(clientId: string): number {
  const id = typeof clientId === "string" ? clientId : "";
  // FNV-1a, 32-bit. Math.imul keeps the multiply from losing precision.
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash = Math.imul(hash ^ id.charCodeAt(i), 0x01000193);
  }
  return Math.abs(hash % 10);
}

export function presenceInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
